import { useEffect, useRef } from 'react'

const WARNING_MESSAGE = 'Leave this page? Your selected files and unsaved tool changes will be lost.'
const GUARD_STATE_KEY = '__stayInBrowserUnsavedGuard'

function workTarget(target: EventTarget | null) {
  return target instanceof Element ? target.closest('[data-unsaved-work]') : null
}

function shouldTrackControl(target: EventTarget | null) {
  if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement)) return false
  if (!workTarget(target) || target.disabled || ('readOnly' in target && target.readOnly)) return false
  return !(target instanceof HTMLInputElement && ['button', 'reset', 'submit'].includes(target.type))
}

export function UnsavedWorkGuard() {
  const dirty = useRef(false)
  const sentinelActive = useRef(false)
  const guardedUrl = useRef('')
  const pendingNavigation = useRef('')

  useEffect(() => {
    const pushSentinel = () => {
      if (sentinelActive.current) return
      const currentState = window.history.state && typeof window.history.state === 'object' ? window.history.state : {}
      const currentIndex = typeof currentState.idx === 'number' ? currentState.idx : undefined
      guardedUrl.current = window.location.href
      window.history.pushState({ ...currentState, ...(currentIndex === undefined ? {} : { idx: currentIndex + 1 }), [GUARD_STATE_KEY]: true }, '', guardedUrl.current)
      sentinelActive.current = true
    }

    const markDirty = (event: Event) => {
      const hasDroppedWork = event instanceof DragEvent && Boolean(event.dataTransfer?.files.length || event.dataTransfer?.getData('text'))
      if (!shouldTrackControl(event.target) && !(hasDroppedWork && workTarget(event.target))) return
      if (dirty.current) return
      dirty.current = true
      pushSentinel()
    }

    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty.current) return
      event.preventDefault()
      event.returnValue = ''
    }

    const continuePendingNavigation = () => {
      const href = pendingNavigation.current
      if (!href) return
      pendingNavigation.current = ''
      window.setTimeout(() => window.location.assign(href), 0)
    }

    const popState = () => {
      if (pendingNavigation.current && !dirty.current) {
        sentinelActive.current = false
        continuePendingNavigation()
        return
      }
      if (!dirty.current || !sentinelActive.current) return
      if (window.confirm(WARNING_MESSAGE)) {
        dirty.current = false
        sentinelActive.current = false
        window.setTimeout(() => window.history.back(), 0)
        return
      }
      sentinelActive.current = false
      pushSentinel()
    }

    const linkClick = (event: MouseEvent) => {
      if (!dirty.current || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      const anchor = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>('a[href]') : null
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return
      const destination = new URL(anchor.href, window.location.href)
      if (!['http:', 'https:'].includes(destination.protocol)) return
      if (destination.href === window.location.href || destination.origin === window.location.origin && destination.pathname === window.location.pathname && destination.search === window.location.search) return
      event.preventDefault()
      event.stopImmediatePropagation()
      if (!window.confirm(WARNING_MESSAGE)) return
      dirty.current = false
      pendingNavigation.current = destination.href
      if (sentinelActive.current) {
        sentinelActive.current = false
        window.history.back()
      } else {
        continuePendingNavigation()
      }
    }

    document.addEventListener('input', markDirty, true)
    document.addEventListener('change', markDirty, true)
    document.addEventListener('drop', markDirty, true)
    document.addEventListener('click', linkClick, true)
    window.addEventListener('beforeunload', beforeUnload)
    window.addEventListener('popstate', popState)
    return () => {
      document.removeEventListener('input', markDirty, true)
      document.removeEventListener('change', markDirty, true)
      document.removeEventListener('drop', markDirty, true)
      document.removeEventListener('click', linkClick, true)
      window.removeEventListener('beforeunload', beforeUnload)
      window.removeEventListener('popstate', popState)
    }
  }, [])

  return null
}
